/**
 * Egress Masks - Precomputed Time-Based Blocking Detection
 * Step 2 Optimization: Convert O(n²) trap detection to O(1) bitmap operations
 *
 * WHY: Current approach loops through all aircraft for each placement check.
 * With masks, we precompute departure time windows once and use fast bitmap operations.
 *
 * TRADE-OFF: Memory (store bitmaps) for speed (bitmap AND vs loops)
 *
 * STATUS: Masks are computed and available but NOT YET used in algorithm.
 * The algorithm still uses dynamic wouldBlock() checks (correct but slower).
 * Future optimization: Replace countTrappedAircraft() with isBlockedByEgressMask()
 * in parkingAlgorithm.ts for O(1) filtering.
 */

import {
  intersects,
  makeEmptyMask,
  orInto,
  setRange,
  TOTAL_SLOTS,
} from "./bitmap";
import { ParkingRequest, Placement, Spot } from "./types";

/**
 * Compute departure mask for an aircraft with tow buffer
 *
 * Creates a bitmap representing when an aircraft occupies the egress lane
 * during departure: [departureBucket, departureBucket + towBuffer]
 *
 * WHY: During tow-out, the egress lane is blocked for both the departing
 * aircraft and any spots behind it on the same lane.
 *
 * @param aircraft - Aircraft departing
 * @param towBufferMinutes - Extra time for tow operation (default: 0)
 * @returns Bitmap with bits set for departure window
 *
 * @example
 * // Aircraft departs 14:00, 15min tow buffer
 * // Result: bits 56-57 set (14:00-14:15)
 * computeDepartureMask(aircraft, 15)
 */
export function computeDepartureMask(
  aircraft: ParkingRequest,
  towBufferMinutes: number = 0
): BigUint64Array {
  const mask = makeEmptyMask();

  // Convert tow buffer from minutes to buckets (15-min intervals)
  const towBufferBuckets = Math.floor(towBufferMinutes / 15);

  // Set bits from departure bucket through tow buffer
  // Clamp end to TOTAL_SLOTS-1 to avoid overflow
  const startBucket = aircraft.departureBucket;
  const endBucket = Math.min(
    aircraft.departureBucket + towBufferBuckets,
    TOTAL_SLOTS - 1
  );

  setRange(mask, startBucket, endBucket);

  return mask;
}

/**
 * Compute egress masks for a single spot (multi-lane support)
 *
 * Generates per-lane masks for multi-lane egress support:
 * - For each egress lane, compute separate "blocks" and "blockedBy" masks
 * - Aircraft can choose the lane with least blocking at departure time
 *
 * WHY PER-LANE: Different lanes block different spots. Aircraft will choose
 * the lane with fewest conflicts, so we need separate masks per lane.
 *
 * WHY: Precomputing these masks allows O(1) blocking checks instead of
 * looping through all aircraft on every placement attempt.
 *
 * ALGORITHM:
 * - For each lane's "blocks" mask: Find all aircraft at this spot, OR their departure masks
 * - For each lane's "blockedBy" mask: Find spots blocked by this lane, OR their departure masks
 *
 * @param spot - Spot to compute masks for
 * @param allSpots - All spots (to look up blocking relationships)
 * @param placement - Current placement map
 * @param allAircraft - All aircraft (to look up aircraft by ID)
 * @param towBufferMinutes - Tow buffer to add to departure windows
 * @returns Object with per-lane masks
 *
 * @example
 * // A01 has Lane 2 (blocks []) and Lane 3 (blocks [A02])
 * // Aircraft at A01 departs 14:00
 * // Result:
 * // - Lane 2: blocks = bits 56-57, blockedBy = empty
 * // - Lane 3: blocks = bits 56-57, blockedBy = departure times from spots blocking Lane 3
 */
export function computeEgressMasks(
  spot: Spot,
  allSpots: Spot[],
  placement: Placement,
  allAircraft: ParkingRequest[],
  towBufferMinutes: number = 0
): { perLane: Array<{
  laneId: string;
  blocksSpotIds: string[];
  blocks: BigUint64Array;
  blockedBy: BigUint64Array;
}> } {
  const egressOptions = spot.egressOptions || [];

  if (egressOptions.length === 0) {
    // No egress options = no masks needed
    return { perLane: [] };
  }

  // Compute masks for each lane
  return {
    perLane: egressOptions.map((option) => {
      const blocksMask = makeEmptyMask();
      const blockedByMask = makeEmptyMask();

      // PART 1: Compute "blocks" mask for this lane
      // (when THIS spot blocks others via this lane)
      // Find all aircraft currently placed at this spot
      for (const aircraftId in placement) {
        const placementInfo = placement[aircraftId];

        // Check if aircraft is at this spot
        let isAtThisSpot = false;
        if ("segments" in placementInfo) {
          // Segmented placement: check if any segment uses this spot
          isAtThisSpot = placementInfo.segments.some(
            (seg) => seg.spotId === spot.id
          );
        } else {
          // Simple placement: check if spotId matches
          isAtThisSpot = placementInfo.spotId === spot.id;
        }

        if (!isAtThisSpot) continue;

        // Find the aircraft object
        const aircraft = allAircraft.find((ac) => ac.id === aircraftId);
        if (!aircraft) continue;

        // Compute departure mask and OR into blocks mask
        const departureMask = computeDepartureMask(aircraft, towBufferMinutes);
        orInto(blocksMask, departureMask);
      }

      // PART 2: Compute "blockedBy" mask for this lane
      // (when THIS lane is blocked by others)
      // Find all spots that block THIS spot via THIS lane
      for (const otherSpot of allSpots) {
        const otherEgressOptions = otherSpot.egressOptions || [];

        // Check if any of otherSpot's lanes block us
        const blocksUs = otherEgressOptions.some(
          (opt) => opt.blocksSpotIds.includes(spot.id)
        );
        if (!blocksUs) continue;

        // Find all aircraft at the blocking spot
        for (const aircraftId in placement) {
          const placementInfo = placement[aircraftId];

          // Check if aircraft is at blocking spot
          let isAtBlockingSpot = false;
          if ("segments" in placementInfo) {
            isAtBlockingSpot = placementInfo.segments.some(
              (seg) => seg.spotId === otherSpot.id
            );
          } else {
            isAtBlockingSpot = placementInfo.spotId === otherSpot.id;
          }

          if (!isAtBlockingSpot) continue;

          // Find the aircraft object
          const aircraft = allAircraft.find((ac) => ac.id === aircraftId);
          if (!aircraft) continue;

          // Compute departure mask and OR into blockedBy mask
          const departureMask = computeDepartureMask(aircraft, towBufferMinutes);
          orInto(blockedByMask, departureMask);
        }
      }

      return {
        laneId: option.laneId,
        blocksSpotIds: option.blocksSpotIds,
        blocks: blocksMask,
        blockedBy: blockedByMask,
      };
    }),
  };
}

/**
 * Check if a spot is blocked during a dwell time using precomputed masks
 *
 * Fast O(1) alternative to looping through all aircraft and checking wouldBlock().
 * Uses bitmap intersection to check if dwell overlaps with blocked time windows.
 *
 * Multi-lane logic: Returns true if ALL lanes are blocked (worst case).
 * If ANY lane is clear, the spot is considered available.
 *
 * WHY: Instead of "for each aircraft, check if it blocks me", we just check:
 * "does my dwell time intersect with the precomputed blocked windows?"
 *
 * @param spot - Spot to check
 * @param dwellMask - Time bitmap for proposed dwell
 * @returns true if dwell overlaps with blocked time windows on ALL lanes
 *
 * @example
 * // Lane 2: blocked 14:00-14:15, Lane 3: clear
 * // Aircraft dwells 13:00-15:00
 * // Result: false (Lane 3 is clear, aircraft can use it)
 */
export function isBlockedByEgressMask(
  spot: Spot,
  dwellMask: BigUint64Array
): boolean {
  // If no egress masks computed, assume not blocked
  // WHY: Spot might not have masks yet, or blocking detection disabled
  if (!spot.egressMasks || spot.egressMasks.perLane.length === 0) {
    return false;
  }

  // Check if ALL lanes are blocked
  // WHY: If ANY lane is clear, aircraft can use that lane (return false)
  // Only return true if ALL lanes blocked (no escape route)
  return spot.egressMasks.perLane.every((laneMask) =>
    intersects(dwellMask, laneMask.blockedBy)
  );
}

/**
 * Precompute egress masks for all spots
 *
 * Mutates spots array by adding egressMasks field to each spot.
 * Should be called once after placement is computed, before using
 * mask-based filtering in the algorithm.
 *
 * WHY: Batch computation is more efficient than computing on-demand.
 * Masks stay valid as long as placements don't change.
 *
 * @param spots - All spots (mutated in place with egressMasks)
 * @param placement - Current placement map
 * @param aircraft - All aircraft
 * @param towBufferMinutes - Tow buffer for departure windows
 *
 * @example
 * // After running placement algorithm
 * precomputeAllEgressMasks(spots, placement, aircraft, 15);
 * // Now all spots have egressMasks for fast checking
 */
export function precomputeAllEgressMasks(
  spots: Spot[],
  placement: Placement,
  aircraft: ParkingRequest[],
  towBufferMinutes: number = 0
): void {
  for (const spot of spots) {
    spot.egressMasks = computeEgressMasks(
      spot,
      spots,
      placement,
      aircraft,
      towBufferMinutes
    );
  }
}
